import type { DateRangeTuple } from '#/utils'

export enum NetRechargeConsistent {
  Match = 'MATCH',
  Mismatch = 'MISMATCH',
}

export enum NetRechargeTicketStatus {
  Closed = 'CLOSED',
  None = 'NONE',
  Pending = 'PENDING',
  Processing = 'PROCESSING',
}

export type NetRechargeConsistencyQuery = 'all' | 'match' | 'mismatch'

export type NetRechargeTicketStatusQuery =
  | 'closed'
  | 'none'
  | 'pending'
  | 'processing'

export interface NetRechargeCompareListParams {
  accountId?: string
  adsAccountId?: string
  agency?: number | string
  bizDate?: DateRangeTuple | null | string[]
  consistent?: NetRechargeConsistent
  currency?: string
  groupId?: number
  onlyMismatch?: boolean
  ticketStatus?: NetRechargeTicketStatus
}

export interface NetRechargeCompareApiItem {
  accountName?: string
  adAccountId?: string
  adsAccountId?: string
  agencyNet?: number | string
  bizDate?: string
  currency?: string
  customerId?: number | string
  customerName?: string
  diff?: number | string
  groupId?: number
  groupName?: string
  hasTicket?: boolean
  isConsistent?: boolean
  mediaPlatform?: string
  merchantId?: number | string
  ourNet?: number | string
  oursNet?: number | string
  ticketId?: number
  ticketStatus?: string
  unmappedCount?: number
}

export interface NetRechargeCompareApiSummary {
  mismatchRows?: number
  openableRows?: number
  totalRows?: number
}

export interface NetRechargeCompareApiResponse {
  list?: NetRechargeCompareApiItem[]
  pageIndex?: number
  pageSize?: number
  summary?: NetRechargeCompareApiSummary
  total?: number
}

export interface NetRechargeCompareItem {
  accountId: string
  agency: string
  agentNetRecharge: number
  companyNetRecharge: number
  bizDate?: string
  consistent: NetRechargeConsistent
  currency: string
  customerId: number | string
  customerName: string
  diffAmount: number
  groupId?: number
  hasTicket?: boolean
  id: string
  mediaPlatform?: string
  ticketId?: number
  ticketStatus: NetRechargeTicketStatus
  unmappedCount?: number
}

export interface NetRechargeCompareSummary {
  comparableCount: number
  creatableTicketCount: number
  mismatchCount: number
}

export interface NetRechargeCompareListResult {
  items: NetRechargeCompareItem[]
  summary: NetRechargeCompareSummary
  total: number
}

export type NetRechargeFlowType = 'CLEAR' | 'RECHARGE' | 'REDUCED'

export interface NetRechargeFlowItem {
  bizTime?: string
  bizTimeRaw?: string
  amount: number
  bizNo?: string
  id: string
  type?: NetRechargeFlowType | (string & {})
}

export interface NetRechargeCompareDetailParams {
  adAccountId: string
  currency?: string
  bizDate: string
  groupId: number
  mediaPlatform?: string
}

export type AgentReconciliationCreateTicketParams =
  NetRechargeCompareDetailParams

export interface AgentReconciliationCreateTicketResult {
  existing: boolean
}

export interface NetRechargeAgencyLine {
  /** 东八区时间，解析失败时为空。 */
  bizTime?: string
  amount?: string
  bizTimeRaw?: string
  bizType?: NetRechargeFlowType | (string & {})
  currency?: string
  extJson?: string
  platformTxnId?: string
}

export interface NetRechargeOursLine {
  amount?: string
  bizNo?: string
  createTime?: string
  currency?: string
  type?: NetRechargeFlowType | (string & {})
}

export interface NetRechargeCompareDetailApi {
  accountName?: string
  adAccountId?: string
  agencyLines?: NetRechargeAgencyLine[]
  agencyNet?: string
  currency?: string
  customerName?: string
  diff?: string
  bizDate?: string
  groupId?: number
  groupName?: string
  isConsistent?: boolean
  mediaPlatform?: string
  merchantId?: string
  oursLines?: NetRechargeOursLine[]
  oursNet?: string
  unmappedLines?: NetRechargeAgencyLine[]
}

export interface NetRechargeCompareDetail
  extends Omit<NetRechargeCompareItem, 'customerId' | 'id' | 'ticketStatus'> {
  accountName?: string
  agentFlows: NetRechargeFlowItem[]
  companyOrders: NetRechargeFlowItem[]
  customerId?: number | string
  groupId?: number
  id?: string
  mediaPlatform?: string
  ticketStatus?: NetRechargeTicketStatus
  unmappedFlows: NetRechargeFlowItem[]
}

export interface AgentReconciliationTicketListParams {
  accountId?: string
  adAccountId?: string
  agency?: number | string
  bizDate?: DateRangeTuple | null | string[]
  groupId?: number
  status?: NetRechargeTicketStatus | string
  ticketNo?: string
}

export interface AgentReconciliationTicketApiItem {
  adAccountId?: string
  closedAt?: string
  closedBy?: string
  closeRemark?: string
  createTime?: string
  currency?: string
  customerName?: string
  bizDate?: string
  groupId?: number
  groupName?: string
  id?: number
  mediaPlatform?: string
  merchantId?: string
  snapshotAgencyNet?: string
  snapshotDiff?: string
  snapshotOursNet?: string
  startedAt?: string
  startedBy?: string
  status?: string
  ticketNo?: string
}

export interface AgentReconciliationTicketApiResponse {
  list?: AgentReconciliationTicketApiItem[]
  pageIndex?: number
  pageSize?: number
  total?: number
}

export interface AgentReconciliationTicketDetailParams {
  id?: number
  ticketNo?: string
}

export interface AgentReconciliationTicketDetailApi
  extends AgentReconciliationTicketApiItem {
  agencyLinesSnapshot?: NetRechargeAgencyLine[]
  currentAgencyNet?: string
  currentDiff?: string
  currentIsConsistent?: boolean
  currentMissing?: boolean
  currentOursNet?: string
  oursLinesSnapshot?: NetRechargeOursLine[]
}

export interface AgentReconciliationTicketItem {
  accountId: string
  agency: string
  closedAt?: string
  closedBy?: string
  closeRemark?: string
  bizDate?: string
  createdAt: string
  currency?: string
  customerId: number | string
  customerName: string
  groupId?: number
  id: string
  mediaPlatform?: string
  snapshotAgentNet: number
  snapshotCompanyNet: number
  snapshotDiff: number
  startedAt?: string
  startedBy?: string
  status: NetRechargeTicketStatus
  ticketNo: string
}

export interface AgentReconciliationTicketDetail
  extends AgentReconciliationTicketItem {
  agentFlows: NetRechargeFlowItem[]
  companyOrders: NetRechargeFlowItem[]
  currentAgentNet: number
  currentCompanyNet: number
  currentDiff: number
  currentIsConsistent?: boolean
  currentMissing?: boolean
}

export interface AgentReconciliationTicketListResult {
  items: AgentReconciliationTicketItem[]
  total: number
}

export enum AgentTokenAvailability {
  Available = 'available',
  ExpiryUnknown = 'expiry_unknown',
  Invalid = 'invalid',
  Uncovered = 'uncovered',
}

export enum AgentTokenChangeVsLast {
  Changed = 'changed',
  InvalidUncovered = 'invalid_uncovered',
  Unchanged = 'unchanged',
}

export enum AgentTokenEnableStatus {
  Disabled = 0,
  Enabled = 1,
}

export interface AgentTokenListParams {
  agencySystemId?: string
  belongAgency?: string
  enableStatus?: AgentTokenEnableStatus
  name?: string
  status?: AgentTokenEnableStatus
  tokenDisplay?: AgentTokenAvailability
}

export interface AgentTokenMember {
  agencyId?: number
  agencySystemId?: string
  cnName?: string
  enName?: string
  id?: number
  name?: string
}

export interface AgentTokenApiItem {
  agencySystemId?: string
  changeFlag?: AgentTokenChangeVsLast
  cnName?: string
  code?: string
  coverFinishDate?: string
  createTime?: string
  feishuDisplay?: string
  id: number
  lastCopyEnd?: string
  lastCopyJobCode?: string
  lastFailAt?: string
  lastFailDate?: string
  lastFailReason?: string
  lastFinishedAt?: string
  lastHasFlagger?: boolean
  lastHasToken?: boolean
  lastTokenState?: string
  members?: AgentTokenMember[]
  name: string
  remark?: string
  status: AgentTokenEnableStatus
  timezone?: string
  tokenDisplay?: AgentTokenAvailability
  tokenOwner?: string
  updateTime?: string
}

export interface AgentTokenItem extends AgentTokenApiItem {
  agency: number[]
  belongAgency: string
  coveredCompleteDate?: string
  enableStatus: AgentTokenEnableStatus
  feishuStatus?: string
  lastSuccessAt?: string
  members: AgentTokenMember[]
  owner?: string
  tokenStatus?: AgentTokenAvailability
}

export interface AgentTokenSaveParams {
  agencySystemId?: string
  cnName?: string
  id: number
  members: AgentTokenMember[]
  name: string
  remark?: string
  status?: AgentTokenEnableStatus
  timezone?: string
  tokenOwner?: string
}

export interface AgentTokenListResult {
  items: AgentTokenItem[]
  total: number
}
